import { useModalState } from "@clipboard-health/ui-components";
import { useRequestAgentByPhone } from "@src/appV2/Agents/api/useWorkerProfileByPhone";
import { type IdentifyProvider, IdentifyProviderEnum } from "@src/appV2/Auth/api/types";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { extractPhoneNumberDigits, useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { isAxiosError } from "axios";
import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import { useRequestAuthOtp } from "../../../api/useRequestAuthOtp";
import { useRequestLoginLink } from "../../../api/useRequestLoginLink";
import { type OtpRequestChannel } from "../../../api/useRequestOtp";
import { AuthMethod, AuthOtpErrorCode, type LoginSignUpFormFields } from "../../types";
import { getLoginUrl } from "../utils/getLoginUrl";

export function useAuthWorkflow() {
  const otpModalState = useModalState();
  const signUpInsteadPhoneModalState = useModalState();
  const signUpInsteadEmailModalState = useModalState();
  const phoneNumberExistsModalState = useModalState();
  const reactivationModalState = useModalState();
  const location = useLocation<{ from?: Location }>();

  const emailConfirmationModalState = useModalState();
  const { showErrorToast } = useToast();

  const [identityProvider, setIdentityProvider] = useState<IdentifyProvider>(
    IdentifyProviderEnum.firebase
  );
  const [cognitoIsSignup, setCognitoIsSignup] = useState<boolean | undefined>(undefined);
  const [authMethod, setAuthMethod] = useState(AuthMethod.PHONE);
  const [cognitoMagicLinkIsLoading, setCognitoMagicLinkIsLoading] = useState(false);

  const isCognitoMagicLinkLoginEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_COGNITO_MAGIC_LINK_LOGIN,
    { defaultValue: false }
  );

  const { mutateAsync: requestOtp, isLoading: otpRequestIsLoading } = useRequestAuthOtp({
    onSuccess() {
      otpModalState.openModal();
    },
    onError(error) {
      const { message = "Something went wrong while requesting your OTP code", error: errorCode } =
        error.response?.data ?? {};

      if (errorCode === AuthOtpErrorCode.AGENT_SOFT_DELETED) {
        reactivationModalState.openModal();
        return;
      }

      if (errorCode === AuthOtpErrorCode.AGENT_NOT_FOUND) {
        signUpInsteadPhoneModalState.openModal();
        return;
      }

      showErrorToast(message);
    },
  });

  const { mutate: requestLoginLink, isLoading: loginLinkRequestIsLoading } = useRequestLoginLink({
    onSuccess() {
      emailConfirmationModalState.openModal();
    },
    onError(error) {
      const {
        message = "Something went wrong while requesting your login link",
        error: errorCode,
      } = error.response?.data ?? {};

      if (isAxiosError(error) && error.response?.status === 404) {
        signUpInsteadEmailModalState.openModal();
        return;
      }

      if (errorCode === AuthOtpErrorCode.AGENT_SOFT_DELETED) {
        reactivationModalState.openModal();
        return;
      }

      showErrorToast(message);
    },
  });

  const { mutateAsync: fetchAgentByPhoneNumber } = useRequestAgentByPhone();

  async function requestCognitoMagicLink(email: string): Promise<boolean> {
    setCognitoMagicLinkIsLoading(true);
    try {
      const cognitoAuth = getCognitoSingleton();
      await cognitoAuth.requestMagicLink(email);
      return true;
    } catch (error) {
      const isErrorInstance = error instanceof Error;
      const mappedError = error as { code?: string; message?: string };
      const errorMessage = isErrorInstance
        ? error.message
        : (mappedError.message ?? JSON.stringify(error));
      logError(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_REQUEST_FAILED, {
        error: isErrorInstance ? error : new Error(errorMessage),
        metadata: {
          hasEmail: Boolean(email),
          errorMessage,
          errorCode: isErrorInstance ? error.name : (mappedError.code ?? "unknown"),
        },
      });
      return false;
    } finally {
      setCognitoMagicLinkIsLoading(false);
    }
  }

  async function handleEmailLogin(email: string): Promise<void> {
    if (isCognitoMagicLinkLoginEnabled) {
      const cognitoSuccess = await requestCognitoMagicLink(email);
      if (cognitoSuccess) {
        emailConfirmationModalState.openModal();
        return;
      }

      logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_FALLBACK_TO_FIREBASE, {
        hasEmail: Boolean(email),
      });
    }

    /**
     * FIXME - remove this unusual side-effect.
     * https://linear.app/clipboardhealth/issue/FEF-99/authv2-via-email-should-work-on-android
     * This is a hack to make legacy v1 email auth work on Android devices
     */
    localStorage.setItem("emailToVerify", email);
    requestLoginLink({
      email,
      url: getLoginUrl({
        email,
        redirectUri: location.state?.from?.pathname
          ? `${location.state.from.pathname}${location.state.from?.search ?? ""}`
          : undefined,
      }),
    });
  }

  async function initiateLogin(values: LoginSignUpFormFields) {
    const { phoneNumber, email } = values;

    if (phoneNumber) {
      const {
        data: { identityProvider, isSignup: responseIsSignup },
      } = await requestOtp({
        isSignup: false,
        phone: extractPhoneNumberDigits(phoneNumber),
      });

      setIdentityProvider(identityProvider);
      setCognitoIsSignup(responseIsSignup);
    } else if (email) {
      await handleEmailLogin(email);
    } else {
      throw new Error("Must provide phone number or email");
    }
  }

  async function initiateSignUp(values: LoginSignUpFormFields) {
    const { phoneNumber } = values;
    if (!phoneNumber) {
      throw new Error("Must provide phone number");
    }

    const unformattedPhoneNumber = extractPhoneNumberDigits(phoneNumber);

    const agent = await fetchAgentByPhoneNumber({
      phone: extractPhoneNumberDigits(unformattedPhoneNumber),
    });

    if (typeof agent.data === "object" && agent.data?.onboardingFlags?.isSignupCompleted) {
      phoneNumberExistsModalState.openModal();
      return;
    }

    const {
      data: { identityProvider, isSignup: responseIsSignup },
    } = await requestOtp({
      isSignup: true,
      phone: extractPhoneNumberDigits(unformattedPhoneNumber),
    });

    setIdentityProvider(identityProvider);
    setCognitoIsSignup(responseIsSignup);
  }

  const resendOtp = useCallback(
    async (params: { phone: string; isSignup: boolean; channel?: OtpRequestChannel }) => {
      const {
        data: { identityProvider, isSignup: responseIsSignup },
      } = await requestOtp({
        phone: extractPhoneNumberDigits(params.phone),
        isSignup: params.isSignup,
        channel: params.channel,
      });

      setIdentityProvider(identityProvider);
      setCognitoIsSignup(responseIsSignup);
    },
    [requestOtp]
  );

  function getLoadingMessage() {
    if (otpRequestIsLoading) {
      return "Sending Text Message...";
    }

    if (loginLinkRequestIsLoading || cognitoMagicLinkIsLoading) {
      return "Emailing Link...";
    }

    return "";
  }

  return {
    initiateLogin,
    initiateSignUp,
    resendOtp,
    otpRequestIsLoading,
    authLoadingMessage: getLoadingMessage(),
    loginLinkRequestIsLoading: loginLinkRequestIsLoading || cognitoMagicLinkIsLoading,
    otpModalState,
    signUpInsteadPhoneModalState,
    signUpInsteadEmailModalState,
    emailConfirmationModalState,
    phoneNumberExistsModalState,
    reactivationModalState,
    authMethod,
    setAuthMethod,
    identityProvider,
    cognitoIsSignup,
  };
}
